import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { FixedSizeList as List } from "react-window";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Search, ChevronDown } from "lucide-react";
import { createPortal } from "react-dom";

interface City {
  id: string;
  name: string;
}

interface VirtualizedCitySelectProps {
  cities: City[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  error?: string;
  withForm?: boolean;
}

const ITEM_HEIGHT = 36; // Height of each city item
const VISIBLE_ITEMS = 8; // Number of items visible at once
const LIST_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS;

const VirtualizedCitySelect: React.FC<VirtualizedCitySelectProps> = ({
  cities,
  value,
  onValueChange,
  placeholder = "Select City",
  disabled = false,
  label = "City",
  error,
  withForm = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<any>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

  // Helper to update dropdown position
  const updateDropdownPosition = () => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: "absolute",
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
        zIndex: 9999,
      });
    }
  };

  // Update input value when external value changes
  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Filter cities based on search term
  const filteredCities = useMemo(() => {
    if (!searchTerm) return cities;
    return cities.filter((city) =>
      city.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [cities, searchTerm]);

  // Row renderer for react-window
  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const city = filteredCities[index];
      if (!city) return null;

      return (
        <div
          style={style}
          className={`h-9 px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 flex items-center ${
            city.name === value ? "bg-gray-100 font-semibold" : ""
          }`}
          onMouseDown={() => {
            onValueChange(city.name);
            setInputValue(city.name);
            setSearchTerm("");
            setIsOpen(false);
          }}
          tabIndex={-1}
        >
          {city.name}
        </div>
      );
    },
    [filteredCities, onValueChange, value]
  );

  // Open dropdown on input focus/click
  const handleInputFocus = () => {
    if (!disabled) setIsOpen(true);
  };

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
  };

  // Keyboard navigation
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" && filteredCities.length > 0) {
      e.preventDefault();
      listRef.current?.scrollToItem(0);
    } else if (e.key === "Enter" && filteredCities.length > 0) {
      e.preventDefault();
      onValueChange(filteredCities[0].name);
      setInputValue(filteredCities[0].name);
      setSearchTerm("");
      setIsOpen(false);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Close dropdown on blur (with a small delay to allow click)
  const handleInputBlur = () => {
    setTimeout(() => setIsOpen(false), 100);
  };

  // Position dropdown absolutely using portal and update on scroll/resize
  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      window.addEventListener("scroll", updateDropdownPosition, true);
      window.addEventListener("resize", updateDropdownPosition);
      return () => {
        window.removeEventListener("scroll", updateDropdownPosition, true);
        window.removeEventListener("resize", updateDropdownPosition);
      };
    }
  }, [isOpen]);

  const inputField = (
    <div className="relative">
      <Input
        ref={inputRef}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onClick={handleInputFocus}
        onKeyDown={handleInputKeyDown}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
      />
      {/* Dropdown rendered in portal */}
      {isOpen &&
        createPortal(
          <div
            style={dropdownStyle}
            className="bg-white border border-gray-200 rounded-md shadow-lg max-h-72 overflow-hidden"
          >
            {filteredCities.length > 0 ? (
              <div
                onWheel={(e) => {
                  // Allow mouse wheel scrolling within the dropdown
                  e.stopPropagation();
                }}
              >
                <List
                  ref={listRef}
                  height={Math.min(
                    LIST_HEIGHT,
                    filteredCities.length * ITEM_HEIGHT
                  )}
                  itemCount={filteredCities.length}
                  itemSize={ITEM_HEIGHT}
                  width={dropdownStyle.width || "100%"}
                >
                  {Row}
                </List>
              </div>
            ) : (
              <div className="p-3 text-center text-gray-500 text-sm">
                {searchTerm ? "No cities found" : "No cities available"}
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );

  if (withForm) {
    return (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        {inputField}
        {error && <FormMessage>{error}</FormMessage>}
      </FormItem>
    );
  }
  return inputField;
};

export default VirtualizedCitySelect;
